import { useId } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  ACCOUNT_FIELDS,
  MAX_ACCOUNTS_PER_METHOD,
  METHODS_WITHOUT_ACCOUNT,
  PAYMENT_METHOD_OPTIONS,
  emptyAccountDraft,
  type PaymentAccountDraft,
  type PaymentMethodDraft,
} from '@/lib/paymentMethods'
import type { PaymentMethodType } from '@/lib/api/botSettings'

interface PaymentMethodEditorProps {
  method: PaymentMethodDraft
  index: number
  error?: string
  disabled?: boolean
  onChange: (next: PaymentMethodDraft) => void
  onRemove: () => void
}

/** Un método de pago del negocio con sus cuentas. No sabe de guardado: edita el borrador que recibe. */
export function PaymentMethodEditor({ method, index, error, disabled = false, onChange, onRemove }: PaymentMethodEditorProps) {
  const baseId = useId()
  const option = PAYMENT_METHOD_OPTIONS.find((candidate) => candidate.value === method.type)
  const needsAccount = !METHODS_WITHOUT_ACCOUNT.has(method.type)

  const update = (patch: Partial<PaymentMethodDraft>) => onChange({ ...method, ...patch })
  const updateAccount = (accountId: string, patch: Partial<PaymentAccountDraft>) =>
    update({ accounts: method.accounts.map((account) => (account.id === accountId ? { ...account, ...patch } : account)) })

  return (
    <section aria-label={`Método de pago ${index + 1}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_8rem]">
        <div>
          <label htmlFor={`${baseId}-type`} className="block text-xs font-semibold text-slate-700">
            Tipo de pago
          </label>
          <select
            id={`${baseId}-type`}
            value={method.type}
            disabled={disabled}
            onChange={(event) => update({ type: event.target.value as PaymentMethodType })}
            className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#075E54]/30"
          >
            {PAYMENT_METHOD_OPTIONS.map((candidate) => (
              <option key={candidate.value} value={candidate.value}>
                {candidate.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${baseId}-label`} className="block text-xs font-semibold text-slate-700">
            Nombre que verá el cliente
          </label>
          <Input
            id={`${baseId}-label`}
            value={method.label}
            maxLength={60}
            disabled={disabled}
            onChange={(event) => update({ label: event.target.value })}
            placeholder="Ej.: Pago Móvil Banesco"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${baseId}-error` : undefined}
            className="mt-1"
          />
        </div>
        <div>
          <label htmlFor={`${baseId}-currency`} className="block text-xs font-semibold text-slate-700">
            Moneda
          </label>
          <Input
            id={`${baseId}-currency`}
            value={method.currency}
            maxLength={20}
            disabled={disabled}
            onChange={(event) => update({ currency: event.target.value })}
            placeholder="USD, VES"
            className="mt-1"
          />
        </div>
      </div>
      {option && <p className="mt-1.5 text-[11px] text-slate-500">{option.hint}</p>}
      {error && (
        <p id={`${baseId}-error`} role="alert" className="mt-1.5 text-xs font-medium text-rose-700">
          {error}
        </p>
      )}

      {needsAccount && (
        <div className="mt-3 space-y-3">
          {method.accounts.map((account, accountIndex) => (
            <fieldset key={account.id} className="rounded-lg border border-slate-100 bg-slate-50/70 p-3">
              <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Cuenta {accountIndex + 1}
              </legend>
              <div className="grid gap-2.5 sm:grid-cols-2">
                {ACCOUNT_FIELDS.map((field) => (
                  <div key={field.key} className={field.key === 'notes' ? 'sm:col-span-2' : undefined}>
                    <label htmlFor={`${baseId}-${account.id}-${field.key}`} className="block text-[11px] font-semibold text-slate-600">
                      {field.label}
                    </label>
                    <Input
                      id={`${baseId}-${account.id}-${field.key}`}
                      type={field.inputMode === 'email' ? 'email' : 'text'}
                      inputMode={field.inputMode}
                      value={account[field.key]}
                      maxLength={field.maxLength}
                      disabled={disabled}
                      onChange={(event) => updateAccount(account.id, { [field.key]: event.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                ))}
              </div>
              {method.accounts.length > 1 && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => update({ accounts: method.accounts.filter((candidate) => candidate.id !== account.id) })}
                  className="mt-2 text-xs font-medium text-rose-700 hover:underline disabled:opacity-50"
                >
                  Quitar esta cuenta
                </button>
              )}
            </fieldset>
          ))}
          {method.accounts.length < MAX_ACCOUNTS_PER_METHOD && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => update({ accounts: [...method.accounts, emptyAccountDraft()] })}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#075E54] hover:underline disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Agregar otra cuenta
            </button>
          )}
        </div>
      )}

      <div className="mt-3">
        <label htmlFor={`${baseId}-instructions`} className="block text-[11px] font-semibold text-slate-600">
          Indicaciones adicionales (opcional)
        </label>
        <textarea
          id={`${baseId}-instructions`}
          value={method.instructions}
          maxLength={300}
          rows={2}
          disabled={disabled}
          onChange={(event) => update({ instructions: event.target.value })}
          placeholder="Ej.: Envía el capture al terminar el pago."
          className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#075E54]/30"
        />
      </div>

      <div className="mt-3 flex justify-end">
        <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={onRemove} className="text-rose-700 hover:text-rose-800">
          <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Quitar este método
        </Button>
      </div>
    </section>
  )
}
